/**
 * Building blocks of the Analyses page's run-details drawer, shared by the
 * single-run body (`AnalysisRunDetailsDrawer`) and the paired-comparison body
 * (`ComparisonRunDetails`).
 */
import { InfoHexagonSolid } from '@mynaui/icons-react';
import { Check, Copy, Download, ExternalLink, Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { LayerData } from '@/atoms/layers.atom';
import { Duration } from '@/components/Duration';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ProcessJobStatus, type AnalysisRunDetailsQuery } from '@/graphql/graphql';
import { formatFileSize, type AnalysisRunFileItem } from '@/lib/analyses/runList';
import { isOptionalStepType } from '@/lib/analyses/runs';
import { translateKey } from '@/lib/analyses/translateKey';
import { cn } from '@/lib/utils';

import { RunStatusIcon } from './RunStatusIcon';

export type RunStep = AnalysisRunDetailsQuery['batch']['jobs'][number];

/** Parameters beyond this many sit behind "Show all". */
export const PARAMS_COLLAPSE_AFTER = 8;

export const muted = 'text-sm text-slate-500 dark:text-stone-400';

/** A 1 s clock while the run is active (live duration), frozen otherwise. */
export function useDrawerClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

export function Section({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <section data-testid={testId} className='flex flex-col gap-2'>
      <h3 className='text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-stone-400'>{title}</h3>
      {children}
    </section>
  );
}

export function Field({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className='flex min-w-0 flex-col'>
      <dt className='text-xs text-slate-500 dark:text-stone-400'>{label}</dt>
      <dd data-testid={testId} className='min-w-0 truncate text-sm font-medium text-slate-900 dark:text-stone-100'>
        {children}
      </dd>
    </div>
  );
}

/** A failure's full text, selectable, with a Copy button. */
export function ErrorBox({ error, testId, copyTestId }: { error: string; testId: string; copyTestId: string }) {
  const { t } = useTranslation('project');
  const [copied, setCopied] = useState(false);
  const copyError = () => {
    navigator.clipboard?.writeText(error).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {},
    );
  };
  return (
    <div className='mt-1 rounded-md border border-rose-200 bg-rose-50 p-2 dark:border-rose-900/60 dark:bg-rose-950/40'>
      <pre
        data-testid={testId}
        className='max-h-40 select-text overflow-auto whitespace-pre-wrap break-words font-sans text-xs text-rose-700 dark:text-rose-300'>
        {error}
      </pre>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        data-testid={copyTestId}
        className='mt-1 h-7 px-2 text-xs'
        onClick={copyError}>
        {copied ? <Check className='size-3.5 text-emerald-500' /> : <Copy className='size-3.5' />}
        <span className='ml-1'>{copied ? t('analyses.details.copied') : t('analyses.details.copyError')}</span>
      </Button>
    </div>
  );
}

/** The run's input layers; a deleted one reads "—". */
export function InputList({
  layerIds,
  layersById,
  onOpenLayer,
}: {
  layerIds: readonly string[];
  layersById: ReadonlyMap<string, LayerData>;
  onOpenLayer: (layerId: string) => void;
}) {
  const { t } = useTranslation('project');
  if (layerIds.length === 0) return <p className={muted}>{t('analyses.details.noInputs')}</p>;
  return (
    <ul className='flex flex-col gap-1'>
      {layerIds.map((layerId) => {
        const layer = layersById.get(layerId);
        return (
          <li key={layerId} data-testid={`analysis-run-details-input-${layerId}`}>
            {layer ? (
              <button
                type='button'
                onClick={() => onOpenLayer(layerId)}
                className='flex max-w-full items-center gap-1.5 truncate rounded text-left text-sm text-blue-700 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-blue-500 dark:text-blue-300'>
                <ExternalLink className='size-3.5 shrink-0' aria-hidden />
                <span className='truncate' title={layer.name}>
                  {layer.name}
                </span>
              </button>
            ) : (
              <span className={muted}>
                — <span className='text-xs italic'>({t('analyses.details.deletedLayer')})</span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Downloadable files, each with its type, size, age and an optional tag
 *  (the comparison part it belongs to). */
export function FileList({
  files,
  relative,
  onDownload,
  tagOf,
}: {
  files: readonly AnalysisRunFileItem[];
  relative: (value: unknown) => string;
  onDownload: (file: AnalysisRunFileItem) => void;
  tagOf?: (file: AnalysisRunFileItem) => string | null;
}) {
  const { t } = useTranslation('project');
  if (files.length === 0) return <p className={muted}>{t('analyses.details.noFiles')}</p>;
  return (
    <ul className='flex flex-col divide-y divide-slate-200/70 dark:divide-stone-800'>
      {files.map((file) => {
        const tag = tagOf?.(file);
        return (
          <li key={file.id} data-testid={`analysis-run-details-file-${file.id}`} className='flex items-center gap-3 py-1.5'>
            <span className='w-16 shrink-0 truncate rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300'>
              {file.type}
            </span>
            <div className='min-w-0 flex-1'>
              <div className='truncate text-sm text-slate-900 dark:text-stone-100' title={file.filename}>
                {file.filename}
              </div>
              <div className='text-xs text-slate-500 dark:text-stone-400'>
                {[tag, formatFileSize(file.contentLength), relative(file.createdAt)].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8'
              data-testid={`analysis-run-details-download-${file.id}`}
              aria-label={`${t('analyses.details.download')}: ${file.filename}`}
              onClick={() => onDownload(file)}>
              <Download className='size-4' />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

export function StepLine({ step, isStaff }: { step: RunStep; isStaff: boolean }) {
  const { t } = useTranslation('project');
  const failed = step.status === ProcessJobStatus.Failed;
  const optional = isOptionalStepType(step.type);
  return (
    <li
      data-testid={`analysis-run-details-step-${step.id}`}
      data-status={step.status}
      className={cn('flex flex-col', step.parentItemId && 'pl-6')}>
      <div className='flex items-center gap-2 text-sm'>
        <span
          role='img'
          aria-label={translateKey(t, `analysisLauncher.runs.status.${step.status}`)}
          className='flex w-4 items-center justify-center [&>svg]:size-4'>
          <RunStatusIcon status={step.status} />
        </span>
        <span className='min-w-0 truncate text-slate-900 dark:text-stone-100'>
          {translateKey(t, `jobTypes.${step.type}`)}
        </span>
        {optional && failed && (
          <span
            data-testid={`analysis-run-details-step-optional-${step.id}`}
            title={t('analyses.details.optionalHint')}
            className='rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-medium text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300'>
            {t('analyses.details.optional')}
          </span>
        )}
        {step.attempts > 1 && (
          <span className='text-xs text-slate-500 dark:text-stone-400'>
            {t('analyses.details.attempts', { count: step.attempts })}
          </span>
        )}
        <span className='ml-auto flex shrink-0 items-center gap-1.5 text-slate-500 dark:text-stone-400'>
          {/* Staff-only CloudWatch link, as on the data centre's job list. */}
          {isStaff && step.logStream && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  data-testid={`run-step-log-stream-${step.id}`}
                  aria-label={t('analyses.details.debugLogs')}
                  onClick={() => window.open(step.logStream!, '_blank', 'noopener')}
                  className='rounded text-slate-400 opacity-70 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-blue-500 dark:text-stone-500'>
                  <InfoHexagonSolid className='size-4 fill-slate-100 dark:fill-stone-700' />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('analyses.details.debugLogs')}</TooltipContent>
            </Tooltip>
          )}
          {step.startedAt ? <Duration startedAt={step.startedAt} completedAt={step.completedAt} /> : null}
        </span>
      </div>
      {failed && step.errorPreview && (
        <p
          data-testid={`analysis-run-details-step-error-${step.id}`}
          className='ml-6 line-clamp-3 select-text whitespace-pre-wrap break-words text-xs text-rose-600 dark:text-rose-400'
          title={step.errorPreview}>
          {step.errorPreview}
        </p>
      )}
    </li>
  );
}

/** The drawer footer's Delete (the confirmation is the caller's). */
export function DeleteRunButton({
  label,
  isDeleting,
  onClick,
}: {
  label: string;
  isDeleting: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type='button'
      variant='destructive'
      size='sm'
      data-testid='analysis-run-details-delete'
      disabled={isDeleting}
      onClick={onClick}>
      <Trash2 className='size-4' />
      <span className='ml-1'>{label}</span>
    </Button>
  );
}
